/*
 * Members, invitations and client orgs: the pure decisions behind the Members tab, the Clients page and the accept
 * page (labels, field validation, what each row offers, how an accept answer reads). The API stays the authority.
 */
import type { AssignableOrgRole, InvitationEmailStatus, Me } from '@hangar/shared';
import { ApiError } from '@/api/client';
import type { Invitation, Member } from '@/api/schemasMembers';

export interface RoleOption {
  value: AssignableOrgRole;
  label: string;
  description: string;
}

/** The roles the console offers (guests, with their projects and end date, are invited through the API for now). */
export const ROLE_OPTIONS: readonly RoleOption[] = [
  { value: 'admin', label: 'Admin', description: 'Everything, including members and integrations' },
  { value: 'member', label: 'Member', description: 'Builds and environment variables; no member management' },
  { value: 'viewer', label: 'Viewer', description: 'Reads builds and configuration; changes nothing' },
];

const ROLE_LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer', guest: 'Guest' };
export const roleLabel = (role: string): string => ROLE_LABEL[role] ?? role;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SLUG = /^[a-z0-9][a-z0-9-]{1,62}$/;

export interface InviteValues {
  email: string;
  name: string;
  role: AssignableOrgRole;
  message: string;
}

export type FieldErrors<K extends string> = Partial<Record<K, string>>;

/** Inline validation of the invite dialog; empty when it may be sent. */
export function validateInvite(v: InviteValues, members: readonly Member[] = [], pending: readonly Invitation[] = []): FieldErrors<'email' | 'message'> {
  const email = v.email.trim().toLowerCase();
  const errors: FieldErrors<'email' | 'message'> = {};
  if (!email) errors.email = 'Enter the email address to invite';
  else if (!EMAIL.test(email)) errors.email = 'Enter a valid email address, like name@company.com';
  else if (members.some((m) => m.user.email === email)) errors.email = 'This person is already a member';
  else if (pending.some((i) => i.email === email)) errors.email = 'An invitation for this email is pending: resend it instead';
  if (v.message.length > 1000) errors.message = 'At most 1000 characters';
  return errors;
}

export interface ClientValues {
  name: string;
  slug: string;
  adminEmail: string;
  adminName: string;
}

/** `QA Client Co` → `qa-client-co` (the slug field follows the name until edited). */
export function slugFromName(name: string): string {
  return name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63);
}

export function validateClient(v: ClientValues): FieldErrors<'name' | 'slug' | 'adminEmail'> {
  const errors: FieldErrors<'name' | 'slug' | 'adminEmail'> = {};
  if (!v.name.trim()) errors.name = 'Enter the client\'s name';
  if (!SLUG.test(v.slug)) errors.slug = 'Lowercase letters, digits and hyphens, 2 to 63 characters';
  if (!EMAIL.test(v.adminEmail.trim())) errors.adminEmail = 'Enter the first admin\'s work email';
  return errors;
}

/** Maps an API field error to the client form's fields (`firstAdmin` is the admin email). */
export function clientFieldOf(field: string): keyof ClientValues | undefined {
  if (field === 'firstAdmin' || field === 'emailDomains') return 'adminEmail';
  return field === 'name' || field === 'slug' ? field : undefined;
}

export interface RowCan {
  allowed: boolean;
  reason?: string;
}

/** What a member row offers: never your own role, never the owner (Malible), never an external guest's role. */
export function memberRowCan(member: Member, me: Me | undefined): { changeRole: RowCan; remove: RowCan } {
  if (member.role === 'owner') {
    const reason = 'The owner role is managed by your Hangar administrators';
    return { changeRole: { allowed: false, reason }, remove: { allowed: false, reason } };
  }
  if (me?.user?.id === member.user.id) {
    const reason = 'Ask another admin to change your own access';
    return { changeRole: { allowed: false, reason }, remove: { allowed: false, reason } };
  }
  const changeRole = member.user.isExternal ? { allowed: false, reason: 'An external user is always a guest' } : { allowed: true };
  return { changeRole, remove: { allowed: true } };
}

/** What became of an invitation's email, as the console names it. */
export const EMAIL_STATUS_LABEL: Record<InvitationEmailStatus, string> = {
  sent: 'Email sent', sign_in_sent: 'Sign-in email sent', failed: 'Email failed', bounced: 'Email bounced',
};

/** `3 min ago`, `3 h ago`, `3 days ago` or `just now`. */
function ago(at: number, now: number): string {
  const minutes = Math.max(0, Math.round((now - at) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} h ago` : `${Math.round(hours / 24)} days ago`;
}

/** The invitation's email status; an invitation older than the field is `sent` when it has a send, else unknown. */
export const emailStatusOf = (i: Invitation): InvitationEmailStatus | null => i.emailStatus ?? (i.lastSentAt ? 'sent' : null);

/** Whether the email row needs attention (failed, bounced, or never sent): shown in the destructive color. */
export const emailNeedsAttention = (i: Invitation): boolean => {
  const status = emailStatusOf(i);
  return status === null || status === 'failed' || status === 'bounced';
};

/** The Email cell of an invitation: what became of the last email, and when it went out. */
export function invitationSentLabel(i: Invitation, now: number): string {
  const status = emailStatusOf(i);
  if (status === 'failed') return 'Email failed: resend it';
  if (status === 'bounced') return 'Email bounced: check the address, then resend';
  const at = Date.parse(i.lastSentAt ?? '');
  if (status === null || Number.isNaN(at)) return 'Email not sent yet: resend it';
  return status === 'sign_in_sent' ? `Sign-in email sent ${ago(at, now)}` : `Sent ${ago(at, now)}`;
}

/** The message of a 429 (the org's daily cap or the resend cooldown), or undefined for any other failure. */
export function rateLimitMessage(e: unknown): string | undefined {
  return e instanceof ApiError && e.status === 429 ? e.message : undefined;
}

/** The toast after inviting or resending: what went out to whom, or that nothing did. */
export function deliveryToast(i: Pick<Invitation, 'email' | 'emailStatus'>): { kind: 'success' | 'warning'; title: string; description?: string } {
  if (i.emailStatus === 'failed') return { kind: 'warning', title: `Invitation for ${i.email} saved, but the email did not go out` };
  if (i.emailStatus === 'sign_in_sent') {
    return { kind: 'success', title: `Sign-in email sent to ${i.email}`, description: 'It carries their sign-in details; their first sign-in with this address accepts the invitation.' };
  }
  return { kind: 'success', title: `Invitation sent to ${i.email}` };
}

export type AcceptView =
  | { kind: 'joined'; orgName: string; role: string }
  | { kind: 'already' }
  | { kind: 'invalid'; title: string; description: string };

/** How an accept answer reads on the accept page. */
export function acceptViewOf(result: { orgName: string; role: string } | { error: unknown }): AcceptView {
  if ('orgName' in result) return { kind: 'joined', orgName: result.orgName, role: roleLabel(result.role) };
  const e = result.error;
  if (e instanceof ApiError && e.status === 409) return { kind: 'already' };
  if (e instanceof ApiError && e.status === 403) return { kind: 'invalid', title: 'This invitation is for another email address', description: `${e.message}. Sign out, then open the link again and sign in with the address the invitation was sent to.` };
  if (e instanceof ApiError && (e.status === 410 || e.status === 404 || e.status === 422)) {
    return { kind: 'invalid', title: 'This invitation link no longer works', description: 'It expired, was canceled or was replaced by a newer email. Ask an admin of the organization to send it again.' };
  }
  return { kind: 'invalid', title: 'Could not accept the invitation', description: e instanceof Error ? e.message : 'Unexpected error: try the link again.' };
}

/** The token of the accept link (`#token=hgr_inv_…`), or undefined. */
export function tokenFromHash(hash: string): string | undefined {
  const token = new URLSearchParams(hash.replace(/^#/, '')).get('token') ?? undefined;
  return token && /^hgr_inv_[A-Za-z0-9_-]{20,100}$/.test(token) ? token : undefined;
}
